import { ComponentProps } from 'react';

import { MultiSelectFilter } from '@zendesk/forethought-elements';
import { getReadableNoResponseString } from 'src/components/dashboard-pages/solve-insights/helpers';
import { InsightsTab } from 'src/components/dashboard-pages/solve-insights/types';
import i18n from 'src/locales';

export const ALL_INTENTS = 'all_intents';
export const ALLTIME_START = '0';
export const ALLTIME_END = '9999999999';
export const NA = 'n/a';
export const NA_LABEL = 'N/A';
export const SMALL_CELL_WIDTH = 161;
export const AUTOMATED_CELL_WIDTH = 170;
export const STICKY_HEADER_HEIGHT = 80;
export const TOTAL_STICKY_HEADERS_HEIGHT = 219;
export const TOTAL_STICKY_HEADERS_HEIGHT_REVAMPED = 194;
export const TOTAL_STICKY_HEADERS_HEIGHT_ARTICLES = 309;
// Shared helpers below are imported by several feature surfaces (solve-insights,
// orchestrator, voice-preview), so they bind their own namespaced `t` from the
// shared i18n instance rather than taking a `t` param bound to a caller's namespace.
const t = i18n.getFixedT(null, 'intent-conversation-analytics');

export const getUserEngagementCopy = () => ({
  no: t('drawerBody.csatResolveNo'),
  yes: t('drawerBody.csatResolveYes'),
});
const getRelevanceCopy = () => ({
  irrelevant: t('constants.relevanceCopy.irrelevant'),
  relevant: t('constants.relevanceCopy.relevant'),
  somewhatRelevant: t('constants.relevanceCopy.somewhatRelevant'),
});
// Assisted escalations are presented as non-automated resolutions in the designs.
const getResolutionTierCopy = () =>
  ({
    assisted: t('constants.resolutionTierCopy.assisted'),
    contained: t('constants.resolutionTierCopy.contained'),
    non_automated: t('constants.resolutionTierCopy.assisted'),
    pending: t('constants.resolutionTierCopy.pending'),
    unknown_resolution_tier: NA,
    verified: t('constants.resolutionTierCopy.verified'),
  }) as const;

export const getResolutionTierLabel = (resolutionTier?: string | null) => {
  if (!resolutionTier) {
    return NA;
  }

  const copy = getResolutionTierCopy();
  return copy[resolutionTier as keyof typeof copy] ?? NA;
};
export const getResolutionAutomatedLabel = (
  resolutionAutomated?: boolean | null,
) => {
  if (resolutionAutomated == null) {
    return NA;
  }

  return resolutionAutomated
    ? t('drawerBody.csatResolveYes')
    : t('drawerBody.csatResolveNo');
};
const getQueryCopy = () => ({
  with: t('constants.queryCopy.with'),
  without: t('constants.queryCopy.without'),
});
const getWorkflowTypesCopy = () => ({
  autoflows: t('constants.workflowTypesCopy.autoflows'),
  classic: t('constants.workflowTypesCopy.classic'),
});
const getCsatRatings = () => ({
  five: t('constants.csatRatings.five'),
  four: t('constants.csatRatings.four'),
  notApplicable: NA_LABEL,
  one: t('constants.csatRatings.one'),
  three: t('constants.csatRatings.three'),
  two: t('constants.csatRatings.two'),
});
const getYesNo = () => ({
  false: t('drawerBody.csatResolveNo'),
  true: t('drawerBody.csatResolveYes'),
});

export const getDeflectionInsightFilterOptions = () => {
  const relevanceCopy = getRelevanceCopy();
  const userEngagementCopy = getUserEngagementCopy();
  return [
    {
      label: t('deflectionInsightData.relevance'),
      options: [
        { label: relevanceCopy.relevant, value: 'relevance_ratings.relevant' },
        {
          label: relevanceCopy.somewhatRelevant,
          value: 'relevance_ratings.somewhat relevant',
        },
        {
          label: relevanceCopy.irrelevant,
          value: 'relevance_ratings.irrelevant',
        },
      ],
      value: 'relevance_ratings',
    },
    {
      label: t('deflectionInsightData.userEngagement'),
      options: [
        { label: userEngagementCopy.yes, value: 'dropoff_ratings.false' },
        { label: userEngagementCopy.no, value: 'dropoff_ratings.true' },
      ],
      value: 'dropoff_ratings',
    },
  ];
};

export const getResolvedTierFilterSection = () => {
  const resolutionTierCopy = getResolutionTierCopy();
  return {
    label: t('filterOptions.resolved'),
    // The API treats `n/a` as the filter alias for null or unknown resolution tiers.
    options: [
      {
        label: resolutionTierCopy.contained,
        value: 'resolution_tiers.contained',
      },
      {
        label: resolutionTierCopy.verified,
        value: 'resolution_tiers.verified',
      },
      {
        label: resolutionTierCopy.non_automated,
        value: 'resolution_tiers.non_automated',
      },
      {
        label: NA,
        value: 'resolution_tiers.n/a',
      },
    ],
    value: 'resolution_tiers',
  };
};

export const getResolutionTierFilterOptions = ({
  includeDeflected = false,
}: { includeDeflected?: boolean } = {}) => {
  const yesNo = getYesNo();
  return [
    {
      label: t('resolutionTierInsightsData.automated'),
      options: [
        {
          label: yesNo.true,
          value: 'resolution_automated.true',
        },
        {
          label: yesNo.false,
          value: 'resolution_automated.false',
        },
      ],
      value: 'resolution_automated',
    },
    // Sections read Automated, Deflected, Resolved per the product review;
    // callers that keep their own Deflected section skip it here.
    ...(includeDeflected
      ? [
          {
            label: t('drawerBody.deflectedLabel'),
            options: [
              { label: yesNo.true, value: 'deflection.true' },
              { label: yesNo.false, value: 'deflection.false' },
            ],
            value: 'deflection',
          },
        ]
      : []),
    getResolvedTierFilterSection(),
  ];
};

// Deflection vs. proactive conversations (the `proactive_conversations`
// feature). Sent to the chats-table/card endpoints as a comma-separated
// `conversation_types` string; distinct from the email `conversation_type`
// filter and the voice `channel_mode` Type filter.
export const getConversationTypeFilterOptions = () => [
  {
    label: t('filterOptions.type'),
    options: [
      {
        label: t('filterOptions.typeDeflections'),
        value: 'conversation_types.deflection',
      },
      {
        label: t('filterOptions.typeProactiveConversations'),
        value: 'conversation_types.proactive_conversation',
      },
    ],
    value: 'conversation_types',
  },
];

export const getSentimentInsightFilterOptions = () => [
  {
    label: t('filterOptions.sentiment'),
    options: [
      {
        label: t('filterOptions.feedbackPositive'),
        value: 'ending_sentiments.positive',
      },
      {
        label: t('filterOptions.sentimentNeutral'),
        value: 'ending_sentiments.neutral',
      },
      {
        label: t('filterOptions.feedbackNegative'),
        value: 'ending_sentiments.negative',
      },
    ],
    value: 'ending_sentiments',
  },
];

export const CSAT_RATINGS_KEY = 'csat_ratings';
export const getInsightsFilterOptions = ({
  extraFilters,
  hideClassicWorkflowType,
  hideCSAT,
  hideDeflection,
  hideQuickFeedback,
  hideWorkflowTypes,
  showConversationStatus,
}: {
  extraFilters: ComponentProps<typeof MultiSelectFilter>['options'];
  hideClassicWorkflowType?: boolean;
  hideCSAT?: boolean;
  hideDeflection?: boolean;
  hideQuickFeedback?: boolean;
  hideWorkflowTypes?: boolean;
  showConversationStatus?: boolean;
}) => {
  const csatRatings = getCsatRatings();
  const queryCopy = getQueryCopy();
  const workflowTypesCopy = getWorkflowTypesCopy();
  const yesNo = getYesNo();
  const options = [
    {
      label: t('filterOptions.allAgentTypes'),
      options: [
        {
          label: workflowTypesCopy.autoflows,
          value: 'autonomous_agent_used.true',
        },
        ...(hideClassicWorkflowType
          ? []
          : [
              {
                label: workflowTypesCopy.classic,
                value: 'autonomous_agent_used.false',
              },
            ]),
      ],
      value: 'autonomous_agent_used',
    },
    ...extraFilters,
    ...(showConversationStatus
      ? [
          {
            label: t('filterOptions.status'),
            options: [
              {
                label: t('filterOptions.inProgress'),
                value: 'conversation_status.in_progress',
              },
              {
                label: t('filterOptions.finished'),
                value: 'conversation_status.finished',
              },
            ],
            value: 'conversation_status',
          },
        ]
      : []),
    ...(hideCSAT
      ? []
      : [
          {
            label: 'CSAT',
            options: [
              { label: csatRatings.five, value: `${CSAT_RATINGS_KEY}.5` },
              { label: csatRatings.four, value: `${CSAT_RATINGS_KEY}.4` },
              { label: csatRatings.three, value: `${CSAT_RATINGS_KEY}.3` },
              { label: csatRatings.two, value: `${CSAT_RATINGS_KEY}.2` },
              { label: csatRatings.one, value: `${CSAT_RATINGS_KEY}.1` },
              {
                label: csatRatings.notApplicable,
                value: `${CSAT_RATINGS_KEY}.null`,
              },
            ],
            value: 'csat_exists',
          },
        ]),
    ...(hideQuickFeedback
      ? []
      : [
          {
            label: t('filterOptions.quickFeedback'),
            options: [
              {
                label: t('filterOptions.quickFeedbackPositive'),
                value: 'quick_feedback.positive',
              },
              {
                label: t('chatEventsSection.negative'),
                value: 'quick_feedback.negative',
              },
              {
                label: t('filterOptions.outboundCallStatusNotAnswered'),
                value: 'quick_feedback.unanswered',
              },
            ],
            value: 'quick_feedback',
          },
          {
            label: t('filterOptions.quickFeedbackComment'),
            options: [
              {
                label: t('filterOptions.quickFeedbackCommentHas'),
                value: 'quick_feedback_comment_exists.true',
              },
              {
                label: t('filterOptions.quickFeedbackCommentNone'),
                value: 'quick_feedback_comment_exists.false',
              },
            ],
            value: 'quick_feedback_comment_exists',
          },
        ]),
    {
      label: t('filterOptions.query'),
      options: [
        { label: queryCopy.with, value: 'query_exists.true' },
        { label: queryCopy.without, value: 'query_exists.false' },
      ],
      value: 'query_exists',
    },
    // Hidden when the caller renders Deflected inside the
    // Automated/Deflected/Resolved group instead.
    ...(hideDeflection
      ? []
      : [
          {
            label: t('filterOptions.deflection'),
            options: [
              { label: yesNo.true, value: 'deflection.true' },
              { label: yesNo.false, value: 'deflection.false' },
            ],
            value: 'deflection',
          },
        ]),
  ];

  const handleFilterOptions = () => {
    if (hideWorkflowTypes) {
      return options.filter(option => option.value !== 'autonomous_agent_used');
    }

    return options;
  };

  return handleFilterOptions();
};

export const getVoiceInsightsFilterOptions = ({
  channelMode,
  isResolutionUiEnabled,
  isVoiceCsatEnabled,
  isVoiceOutboundEnabled,
}: {
  // When set, return the Solve Insights Inbound/Outbound per-mode filter set.
  channelMode?: 'inbound' | 'outbound';
  isResolutionUiEnabled?: boolean;
  isVoiceCsatEnabled?: boolean;
  isVoiceOutboundEnabled?: boolean;
}) => {
  const csatRatings = getCsatRatings();
  const queryCopy = getQueryCopy();
  const relevanceCopy = getRelevanceCopy();
  const yesNo = getYesNo();
  if (channelMode) {
    const isOutbound = channelMode === 'outbound';
    return [
      // With the resolution UI, voice Automated mirrors call eligibility
      // (eligible → automated), so the section is relabeled and grouped as
      // Automated, Deflected, Resolved. Resolutions are inbound-only.
      ...(isResolutionUiEnabled
        ? [
            {
              label: t('resolutionTierInsightsData.automated'),
              options: [
                { label: yesNo.true, value: 'call_eligibility.true' },
                { label: yesNo.false, value: 'call_eligibility.false' },
              ],
              value: 'call_eligibility',
            },
          ]
        : [
            {
              label: t('emailConversationDrawer.eligible'),
              options: [
                { label: yesNo.true, value: 'call_eligibility.true' },
                { label: yesNo.false, value: 'call_eligibility.false' },
              ],
              value: 'call_eligibility',
            },
          ]),
      // Deflected: inbound only. With the resolution UI it moves up next to
      // Automated/Resolved; without it, the legacy Eligible → Query →
      // Deflected order is preserved.
      ...(isResolutionUiEnabled && !isOutbound
        ? [
            {
              label: t('drawerBody.deflectedLabel'),
              options: [
                { label: yesNo.true, value: 'deflection.true' },
                { label: yesNo.false, value: 'deflection.false' },
              ],
              value: 'deflection',
            },
            getResolvedTierFilterSection(),
          ]
        : []),
      {
        label: t('filterOptions.query'),
        options: [
          { label: queryCopy.with, value: 'query_exists.true' },
          { label: queryCopy.without, value: 'query_exists.false' },
        ],
        value: 'query_exists',
      },
      ...(!isResolutionUiEnabled && !isOutbound
        ? [
            {
              label: t('drawerBody.deflectedLabel'),
              options: [
                { label: yesNo.true, value: 'deflection.true' },
                { label: yesNo.false, value: 'deflection.false' },
              ],
              value: 'deflection',
            },
          ]
        : []),
      // Call answered + Engaged: outbound only.
      ...(isOutbound
        ? [
            {
              // Mutually-exclusive call states share one key so multiple
              // selections OR together (e.g. Voicemail + Not answered).
              label: t('filterOptions.callAnswered'),
              options: [
                {
                  label: t('filterOptions.outboundCallStatusAnswered'),
                  value: 'voice_outbound_call_status.answered',
                },
                {
                  label: t('filterOptions.outboundCallStatusVoicemail'),
                  value: 'voice_outbound_call_status.voicemail',
                },
                {
                  label: t('filterOptions.outboundCallStatusNotAnswered'),
                  value: 'voice_outbound_call_status.not_answered',
                },
              ],
              value: 'voice_outbound_call_status',
            },
            {
              // Engaged and Follow-up ask are independent dimensions, so they
              // are separate Yes/No filters rather than one combined group.
              label: t('filterOptions.engaged'),
              options: [
                { label: yesNo.true, value: 'voice_outbound_engaged.true' },
                { label: yesNo.false, value: 'voice_outbound_engaged.false' },
              ],
              value: 'voice_outbound_engaged',
            },
            {
              label: t('filterOptions.followUpAsk'),
              options: [
                {
                  label: yesNo.true,
                  value: 'voice_outbound_user_asked_followup.true',
                },
                {
                  label: yesNo.false,
                  value: 'voice_outbound_user_asked_followup.false',
                },
              ],
              value: 'voice_outbound_user_asked_followup',
            },
          ]
        : []),
      {
        label: t('filterOptions.sentiment'),
        options: [
          {
            label: t('filterOptions.feedbackPositive'),
            value: 'ending_sentiments.positive',
          },
          {
            label: t('filterOptions.sentimentNeutral'),
            value: 'ending_sentiments.neutral',
          },
          {
            label: t('filterOptions.feedbackNegative'),
            value: 'ending_sentiments.negative',
          },
        ],
        value: 'ending_sentiments',
      },
      {
        label: t('deflectionInsightData.relevance'),
        options: [
          {
            label: relevanceCopy.relevant,
            value: 'relevance_ratings.relevant',
          },
          {
            label: relevanceCopy.somewhatRelevant,
            value: 'relevance_ratings.somewhat relevant',
          },
          {
            label: relevanceCopy.irrelevant,
            value: 'relevance_ratings.irrelevant',
          },
        ],
        value: 'relevance_ratings',
      },
    ];
  }

  // Legacy filter set (orchestrator GetConversationsSidebar).
  const options = [
    ...(isVoiceOutboundEnabled
      ? [
          {
            label: t('filterOptions.type'),
            options: [
              {
                label: t('filterOptions.typeInbound'),
                value: 'channel_mode.inbound',
              },
              {
                label: t('filterOptions.typeInboundWebCall'),
                value: 'channel_mode.inbound_web_call',
              },
              {
                label: t('filterOptions.typeOutbound'),
                value: 'channel_mode.outbound',
              },
            ],
            value: 'channel_mode',
          },
        ]
      : []),
    {
      label: t('filterOptions.callEligibility'),
      options: [
        {
          label: t('emailConversationDrawer.eligible'),
          value: 'call_eligibility.true',
        },
        {
          label: t('filterOptions.eligibilityIneligible'),
          value: 'call_eligibility.false',
        },
      ],
      value: 'call_eligibility',
    },
    {
      label: t('drawerBody.deflectedLabel'),
      options: [
        { label: yesNo.true, value: 'deflection.true' },
        { label: yesNo.false, value: 'deflection.false' },
      ],
      value: 'deflection',
    },
    ...(isVoiceOutboundEnabled
      ? [
          {
            label: t('filterOptions.engaged'),
            options: [
              {
                label: yesNo.true,
                value: 'voice_outbound_engaged.true',
              },
              {
                label: yesNo.false,
                value: 'voice_outbound_engaged.false',
              },
            ],
            value: 'voice_outbound_engaged',
          },
          {
            label: t('filterOptions.callAnswered'),
            options: [
              {
                label: yesNo.true,
                value: 'voice_outbound_answered_by_user.true',
              },
              {
                label: yesNo.false,
                value: 'voice_outbound_answered_by_user.false',
              },
            ],
            value: 'voice_outbound_answered_by_user',
          },
          {
            label: t('filterOptions.voicemailLeft'),
            options: [
              {
                label: yesNo.true,
                value: 'voice_outbound_went_to_voicemail.true',
              },
              {
                label: yesNo.false,
                value: 'voice_outbound_went_to_voicemail.false',
              },
            ],
            value: 'voice_outbound_went_to_voicemail',
          },
          {
            label: t('filterOptions.followUpAsk'),
            options: [
              {
                label: yesNo.true,
                value: 'voice_outbound_user_asked_followup.true',
              },
              {
                label: yesNo.false,
                value: 'voice_outbound_user_asked_followup.false',
              },
            ],
            value: 'voice_outbound_user_asked_followup',
          },
        ]
      : []),
    {
      label: t('filterOptions.sentiment'),
      options: [
        {
          label: t('filterOptions.feedbackPositive'),
          value: 'ending_sentiments.positive',
        },
        {
          label: t('filterOptions.sentimentNeutral'),
          value: 'ending_sentiments.neutral',
        },
        {
          label: t('filterOptions.feedbackNegative'),
          value: 'ending_sentiments.negative',
        },
      ],
      value: 'ending_sentiments',
    },
    ...(isVoiceCsatEnabled
      ? [
          {
            label: 'CSAT',
            options: [
              { label: csatRatings.five, value: `${CSAT_RATINGS_KEY}.5` },
              { label: csatRatings.four, value: `${CSAT_RATINGS_KEY}.4` },
              { label: csatRatings.three, value: `${CSAT_RATINGS_KEY}.3` },
              { label: csatRatings.two, value: `${CSAT_RATINGS_KEY}.2` },
              { label: csatRatings.one, value: `${CSAT_RATINGS_KEY}.1` },
              {
                label: csatRatings.notApplicable,
                value: `${CSAT_RATINGS_KEY}.null`,
              },
            ],
            value: 'csat_exists',
          },
        ]
      : []),
  ];

  return options;
};

export const getEmailInsightsFilterOptions = ({
  extraFilters,
  hasCsatData = false,
  hideDeflected = false,
  hideEligible = false,
  hideEmailSent = false,
  type,
}: {
  extraFilters: ComponentProps<typeof MultiSelectFilter>['options'];
  hasCsatData?: boolean;
  hideDeflected?: boolean;
  hideEligible?: boolean;
  hideEmailSent?: boolean;
  type: InsightsTab;
}) => {
  const yesNo = getYesNo();
  const executionStatusOptions = [
    'language_not_enabled',
    'no_response',
    'started',
    'ticket_field_not_found',
    'no_matching_transition',
    'execution_error',
    'zero_multilngual_static_articles',
    'no_plausible_response',
    'workflow_not_found',
    'zero_dynamic_articles',
    'no_paraphrased_answer',
    'zendesk_api_error',
    'user_rate_limited_exceeded',
  ].map(key => ({
    label: getReadableNoResponseString(key),
    value: `unanswered_cause.${key}`,
  }));

  const skipReasonOptions = [
    'responded_to_by_agent',
    'non_email_channel',
    'user_email_not_found',
    'no_intent_predicted',
    'user_rate_limited_exceeded',
    'non_english',
    'email_automation',
    'from_widget_handoff',
    'blocked_email',
    'ticket_closed',
    'out_of_office_reply',
  ].map(key => ({
    label: getReadableNoResponseString(key),
    value: `skip_reason.${key}`,
  }));

  if (type === 'article') {
    return [...extraFilters];
  }
  if (type === 'chat') {
    const defaultFilters = [
      {
        label: t('filterOptions.conversationTypes'),
        options: [
          {
            label: t('filterOptions.conversationTypeEmail'),
            value: 'conversation_type.email',
          },
          {
            label: t('filterOptions.conversationTypeEmailAutoflow'),
            value: 'conversation_type.email_autoflow',
          },
          {
            label: t('filterOptions.conversationTypeInteractiveEmail'),
            value: 'conversation_type.interactive_email',
          },
          {
            label: t('filterOptions.conversationTypeHumanInTheLoop'),
            value: 'conversation_type.human_in_the_loop',
          },
        ],
        value: 'conversation_type',
      },
      // Hidden when the caller renders Deflected inside the
      // Automated/Deflected/Resolved group instead.
      ...(hideDeflected
        ? []
        : [
            {
              label: t('drawerBody.deflectedLabel'),
              options: [
                { label: yesNo.true, value: 'deflection.true' },
                { label: yesNo.false, value: 'deflection.false' },
              ],
              value: 'deflection',
            },
          ]),
      ...(hideEligible
        ? []
        : [
            {
              label: t('emailConversationDrawer.eligible'),
              options: [
                { label: yesNo.true, value: 'eligible.true' },
                { label: yesNo.false, value: 'eligible.false' },
              ],
              value: 'eligible',
            },
          ]),
      ...(hideEmailSent
        ? []
        : [
            {
              label: t('filterOptions.emailSent'),
              options: [
                { label: yesNo.true, value: 'email_sent.true' },
                { label: yesNo.false, value: 'email_sent.false' },
              ],
              value: 'email_sent',
            },
          ]),
      // Dynamic options
      ...(hideDeflected && hideEligible && hideEmailSent ? [] : extraFilters),
      {
        label: t('filterOptions.noResponseReason'),
        options: [...executionStatusOptions, ...skipReasonOptions],
        value: 'unanswered_cause',
      },
      {
        label: t('filterOptions.userFeedback'),
        options: [
          {
            label: t('filterOptions.feedbackPositive'),
            value: 'feedback.positive',
          },
          {
            label: t('filterOptions.feedbackNegative'),
            value: 'feedback.negative',
          },
          { label: NA_LABEL, value: 'feedback.N/A' },
        ],
        value: 'feedback',
      },
      ...(hasCsatData
        ? [
            {
              label: t('emailConversationDrawer.helpdeskCsat'),
              options: [
                {
                  label: t('emailConversationDrawer.csatGood'),
                  value: 'helpdesk_csat.good',
                },
                {
                  label: t('emailConversationDrawer.csatBad'),
                  value: 'helpdesk_csat.bad',
                },
                { label: NA_LABEL, value: 'helpdesk_csat.n/a' },
              ],
              value: 'helpdesk_csat',
            },
          ]
        : []),
    ];

    return hideDeflected && hideEligible && hideEmailSent
      ? [...extraFilters, ...defaultFilters]
      : defaultFilters;
  }
  return [];
};

export const FROM_NUMBER = 'from_number';
export const TO_NUMBER = 'to_number';
